import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { FinanciacionPropiaModel } from '../../../../modules/finanzas/financiacion-propia/financiacion-propia.model';
import { FinanciacionPrivadaModel } from '../../../../modules/finanzas/financiacion-privada/financiacion-privada.model';
import { NotificationService } from '../../../shared/services/notification.service';
import {
  entityFormDialogConfig,
  EntityFormOption,
  ensureOption,
  findOption,
  fromApiDate,
  isBlank,
  isInvalidMonths,
  isMissingNumber,
  numberOrNull,
  optionId,
  serverErrorMessage,
  SETTLEMENT_OPTIONS,
  TERM_LONG,
  TERM_SHORT,
  toApiDate,
} from '../../entity-form/entity-form.utils';
import { OwnFinancingEntityService } from '../../own-financing/services/own-financing-entity.service';
import { PrivateFinancingEntityService } from '../../private-financing/services/private-financing-entity.service';
import { FinancingFormLookupsFacade } from '../financing-form-lookups.facade';

export type FinancingFormKind = 'own' | 'private';

export interface FinancingFormDialogData {
  kind: FinancingFormKind;
  uid?: string;
}

const OWN_SHORT_TERM_ACCOUNTS: EntityFormOption[] = [
  { id: '5133', display: 'Otras deudas a corto plazo con empresas del grupo - 5133' },
  { id: '5134', display: 'Otras deudas a corto plazo con empresas asociadas - 5134' },
  { id: '5135', display: 'Otras deudas a corto plazo con otras partes vinculadas - 5135' },
];

const OWN_LONG_TERM_ACCOUNTS: EntityFormOption[] = [
  { id: '1633', display: 'Otras deudas a largo plazo con empresas del grupo - 1633' },
  { id: '1634', display: 'Otras deudas a largo plazo con empresas asociadas - 1634' },
  { id: '1635', display: 'Otras deudas a largo plazo con otras partes vinculadas - 1635' },
];

const OWN_TERM_OPTIONS: EntityFormOption[] = [
  { id: TERM_SHORT, display: 'Corto plazo' },
  { id: TERM_LONG, display: 'Largo plazo' },
];

const PRIVATE_TERM_OPTIONS: EntityFormOption[] = [
  { id: TERM_SHORT, display: 'Corto plazo — 521' },
  { id: TERM_LONG, display: 'Largo plazo — 171' },
];

type FinancingFormErrors = Partial<Record<
  'duracion' | 'cuentaContableACrear' | 'partner' | 'descripcion' | 'fechaConstitucion' | 'plazo' | 'fechaFinalizacion'
  | 'carencia' | 'importeInicial' | 'importePendiente' | 'comisionApertura' | 'tipoInteres' | 'tipoLiquidaciones' | 'bankAccount', string>>;

@Component({
  selector: 'app-financing-form-dialog',
  templateUrl: './financing-form-dialog.component.html',
  styleUrls: ['../../entity-form/entity-form-dialog.scss'],
  standalone: false,
})
export class FinancingFormDialogComponent implements OnInit, OnDestroy {
  readonly settlementOptions = [...SETTLEMENT_OPTIONS];

  private readonly ownTermOptions = [...OWN_TERM_OPTIONS];
  private readonly privateTermOptions = [...PRIVATE_TERM_OPTIONS];
  private readonly ownShortTermAccounts = [...OWN_SHORT_TERM_ACCOUNTS];
  private readonly ownLongTermAccounts = [...OWN_LONG_TERM_ACCOUNTS];
  private readonly unknownAccounts: EntityFormOption[] = [];

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: FinancingFormErrors = {};

  bankAccounts: EntityFormOption[] = [];
  partners: EntityFormOption[] = [];

  private loaded: any = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: FinancingFormDialogData): Observable<boolean> {
    return dialog.open<FinancingFormDialogComponent, FinancingFormDialogData, boolean>(FinancingFormDialogComponent, entityFormDialogConfig(data))
      .afterClosed()
      .pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<FinancingFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: FinancingFormDialogData,
    private ownEntity: OwnFinancingEntityService,
    private privateEntity: PrivateFinancingEntityService,
    private lookups: FinancingFormLookupsFacade,
    private notify: NotificationService,
  ) {
    this.form = this.fb.group({
      duracion: [null],
      cuentaContableACrear: [null],
      partner: [null],
      descripcion: [''],
      fechaConstitucion: [null],
      plazo: [null],
      fechaFinalizacion: [null],
      carencia: [null],
      importeInicial: [null],
      importePendiente: [null],
      comisionApertura: [null],
      tipoInteres: [null],
      tipoLiquidaciones: [null],
      bankAccount: [null],
    });
  }

  get isOwn(): boolean {
    return this.data.kind === 'own';
  }

  get isEdit(): boolean {
    return !!this.data.uid;
  }

  get entityLabel(): string {
    return this.isOwn ? 'financiación propia' : 'financiación privada';
  }

  get title(): string {
    return (this.isEdit ? 'Editar ' : 'Nueva ') + this.entityLabel;
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return this.loaded.descripcion || 'Modifica los datos de la financiación';
    }
    return this.isOwn ? 'Registra una financiación recibida de un socio o empresa vinculada' : 'Registra una financiación recibida de terceros';
  }

  get icon(): string {
    return this.isOwn ? 'people' : 'group';
  }

  get termOptions(): EntityFormOption[] {
    return this.isOwn ? this.ownTermOptions : this.privateTermOptions;
  }

  get accountOptions(): EntityFormOption[] {
    const duracion = Number(optionId(this.form.get('duracion').value));
    if (duracion === TERM_SHORT) return this.ownShortTermAccounts;
    if (duracion === TERM_LONG) return this.ownLongTermAccounts;
    return this.unknownAccounts;
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  get partnerControl(): UntypedFormControl {
    return this.control('partner');
  }

  get accountControl(): UntypedFormControl {
    return this.control('cuentaContableACrear');
  }

  get bankAccountControl(): UntypedFormControl {
    return this.control('bankAccount');
  }

  ngOnInit(): void {
    if (this.isOwn) {
      this.form.get('duracion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        const account = optionId(this.accountControl.value);
        if (account !== null && !findOption(this.accountOptions, account)) {
          this.accountControl.setValue(null);
        }
      });
    }
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isOwn
      ? (this.isEdit ? this.ownEntity.update(this.data.uid, payload) : this.ownEntity.create(payload))
      : (this.isEdit ? this.privateEntity.update(this.data.uid, payload) : this.privateEntity.create(payload));

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Financiación actualizada correctamente' : 'Financiación creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, this.isEdit
          ? `Ha ocurrido un error inesperado al actualizar la ${this.entityLabel}`
          : `Ha ocurrido un error inesperado al crear la ${this.entityLabel}`);
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<any> = !this.isEdit
      ? Promise.resolve(null)
      : this.isOwn ? this.ownEntity.get(this.data.uid) : this.privateEntity.get(this.data.uid);
    const partners = this.isOwn ? this.lookups.partners() : Promise.resolve([] as EntityFormOption[]);

    Promise.all([record, this.lookups.bankAccounts(), partners])
      .then(([financing, bankAccounts, partnerList]) => {
        this.bankAccounts = bankAccounts;
        this.partners = partnerList;
        this.loaded = financing;
        if (financing) this.applyRecord(financing);
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(financing: any): void {
    const partner = this.isOwn
      ? ensureOption(this.partners, financing.partnersId, financing.partnersName)
      : null;

    this.form.patchValue({
      duracion: ensureOption(this.termOptions, numberOrNull(financing.duracion)),
      partner,
      descripcion: financing.descripcion || '',
      fechaConstitucion: fromApiDate(financing.fechaConstitucion),
      plazo: numberOrNull(financing.plazo),
      fechaFinalizacion: fromApiDate(financing.fechaFinalizacion),
      carencia: numberOrNull(financing.carencia),
      importeInicial: numberOrNull(financing.importeInicial),
      importePendiente: numberOrNull(financing.importePendiente),
      comisionApertura: numberOrNull(financing.comisionApertura),
      tipoInteres: numberOrNull(financing.tipoInteres),
      tipoLiquidaciones: ensureOption(this.settlementOptions, numberOrNull(financing.tipoLiquidaciones)),
      bankAccount: ensureOption(this.bankAccounts, financing.bankAccountsUid, financing.bankAccountInfo),
    }, { emitEvent: false });

    if (this.isOwn) {
      const account = ensureOption(this.accountOptions, financing.cuentaContableACrear);
      this.accountControl.setValue(account, { emitEvent: false });
    }
  }



  private validate(): FinancingFormErrors {
    const value = this.form.getRawValue();
    const errors: FinancingFormErrors = {};

    if (optionId(value.duracion) === null) errors.duracion = 'Selecciona la duración';
    if (this.isOwn) {
      if (optionId(value.cuentaContableACrear) === null) errors.cuentaContableACrear = 'Selecciona la cuenta contable';
      if (optionId(value.partner) === null) errors.partner = 'Selecciona un socio de la lista';
    }
    if (isBlank(value.descripcion)) errors.descripcion = 'Indica la descripción';
    if (!value.fechaConstitucion) errors.fechaConstitucion = 'Indica la fecha de constitución';
    if (isInvalidMonths(value.plazo)) errors.plazo = 'Indica el plazo en meses';
    if (!value.fechaFinalizacion) errors.fechaFinalizacion = 'Indica la fecha de finalización';
    if (isInvalidMonths(value.carencia)) errors.carencia = 'Indica la carencia en meses';
    if (isMissingNumber(value.importeInicial)) errors.importeInicial = 'Indica el importe inicial';
    if (isMissingNumber(value.importePendiente)) errors.importePendiente = 'Indica el importe pendiente';
    if (isMissingNumber(value.comisionApertura)) errors.comisionApertura = 'Indica la comisión de apertura';
    if (isMissingNumber(value.tipoInteres)) errors.tipoInteres = 'Indica el tipo de interés';
    if (optionId(value.tipoLiquidaciones) === null) errors.tipoLiquidaciones = 'Selecciona el tipo de liquidaciones';
    if (optionId(value.bankAccount) === null) errors.bankAccount = 'Selecciona la cuenta de adeudo';

    return errors;
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const common = {
      duracion: Number(optionId(value.duracion)),
      descripcion: String(value.descripcion).trim(),
      fechaConstitucion: toApiDate(value.fechaConstitucion),
      plazo: Number(value.plazo),
      fechaFinalizacion: toApiDate(value.fechaFinalizacion),
      carencia: Number(value.carencia),
      importeInicial: Number(value.importeInicial),
      importePendiente: Number(value.importePendiente),
      comisionApertura: Number(value.comisionApertura),
      tipoInteres: Number(value.tipoInteres),
      tipoLiquidaciones: Number(optionId(value.tipoLiquidaciones)),
      bankAccountsUid: optionId(value.bankAccount),
    };

    if (!this.isOwn) {
      const payload: FinanciacionPrivadaModel = { ...base, ...common };
      return payload;
    }

    const payload: FinanciacionPropiaModel = {
      ...base,
      ...common,
      cuentaContableACrear: String(optionId(value.cuentaContableACrear)),
      partnersId: Number(value.partner.id),
      partnersName: value.partner.display,
    };
    if (this.loaded && Number(this.loaded.duracion) !== payload.duracion) {
      payload.accountingModelsCortoPlazoNumber = null;
      payload.accountingModelsLargoPlazoNumber = null;
    }
    return payload;
  }
}
